import type { ReminderData } from '../store/schema/ReminderData';
import {
    raiseNotification,
    NotificationContext,
    NotificationDisplayType,
    NotificationAction,
    NotificationCustomAction,
    ButtonProperty,
    ActionProperty,
    DropdownAction,
    eventReminderNotification,
    ActionType,
} from 'owa-client-pie/lib/notification.g';
import { isPieHostBridgeInitialized, isPieNamespaceGTE } from 'owa-client-pie';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import type NewMailNotificationPayload from 'owa-service/lib/contract/NewMailNotificationPayload';
import { asDate, formatWeekDayTime } from 'owa-datetime';
import { EventReminderActionIds, NewEmailActionIds } from './ButtonId';
import { reminder_snooze_header } from '../components/reminders/ReminderForCalendarOrTask.locstring.json';
import loc from 'owa-localize';
import {
    notification_dismiss_title,
    notification_flag_title,
    notification_snoozedropdown_title,
    notification_5minsnoozedropdown_title,
    notification_10minsnoozedropdown_title,
} from './windowsNotificationStrings.locstring.json';
import { isFeatureEnabled } from 'owa-feature-flags';
import { NotificationTypes } from './NotificationTypes';
import { getCdnUrl } from 'owa-config';

export function raiseEventNotification(reminders: ReminderData[]) {
    // Send reminder notification pie event to the native host app if isShown and the pieBridge is initialized
    if (isPieHostBridgeInitialized() && isFeatureEnabled('auth-mon-reminderNotifications')) {
        let customAction = [
            {
                actionType: ActionType.DropdownAction,
                button: {
                    buttonId: EventReminderActionIds.SnoozeButton,
                    iconUrl: getActionIconUrl('Snooze'),
                    displayText: loc(reminder_snooze_header),
                } as ButtonProperty,
                action: {
                    buttonId: EventReminderActionIds.SnoozeDropdownOptions,
                    iconUrl: '',
                    displayText: loc(notification_snoozedropdown_title),
                    items: [
                        {
                            displayContent: loc(notification_5minsnoozedropdown_title),
                            id: '5',
                        } as DropdownAction,
                        {
                            displayContent: loc(notification_10minsnoozedropdown_title),
                            id: '10',
                        } as DropdownAction,
                    ],
                } as ActionProperty,
            } as NotificationCustomAction,
            {
                actionType: ActionType.ButtonAction,
                button: {
                    buttonId: EventReminderActionIds.DismissButton,
                    iconUrl: getActionIconUrl('Dissmiss'),
                    displayText: loc(notification_dismiss_title),
                } as ButtonProperty,
            } as NotificationAction,
        ];
        reminders.forEach(rem => {
            eventReminderNotification({
                subject: rem.subject,
                startTime: new Date(asDate(rem.startTime)),
                endTime: new Date(asDate(rem.endTime)),
                location: rem.location,
                itemId: rem.itemId.Id,
            });
            // Call the new api only if the notification schema is greater than 0.2
            if (isPieNamespaceGTE('Notification', 0.2)) {
                let notificationContext = {
                    notificationDisplayType:
                        NotificationDisplayType.ThreeActionNotificationWithDropdown,
                    text: [rem.subject, rem.location, formatWeekDayTime(rem.startTime)],
                    notificationType: NotificationTypes.EventReminder,
                    originSmtpEmailAddress: '',
                    isFutureNotification: false,
                    scheduledTime: null,
                    itemId: rem.itemId.Id,
                    originName: '',
                    upn: getUserConfiguration().SessionSettings?.UserPrincipalName,
                    notificationActions: customAction,
                    isSilent: false,
                    originDisplayImageUrl: '',
                } as NotificationContext;
                raiseNotification(notificationContext);
            }
        });
    }
}

export function raiseNewEmailNotification(newEmailNotifications: NewMailNotificationPayload) {
    // Send reminder notification pie event to the native host app if isShown and the pieBridge is initialized
    if (isPieHostBridgeInitialized() && isFeatureEnabled('auth-mon-newEmailNotifications')) {
        let customAction = [
            {
                actionType: ActionType.ButtonAction,
                button: {
                    buttonId: NewEmailActionIds.SetFlagButton,
                    iconUrl: getActionIconUrl('Flag'),
                    displayText: loc(notification_flag_title),
                } as ButtonProperty,
            } as NotificationAction,
            {
                actionType: ActionType.ButtonAction,
                button: {
                    buttonId: NewEmailActionIds.DismissButton,
                    iconUrl: getActionIconUrl('Dismiss'),
                    displayText: loc(notification_dismiss_title),
                } as ButtonProperty,
            } as NotificationAction,
        ];
        let notificationContext = {
            notificationDisplayType: NotificationDisplayType.ThreeActionNotificationWithImage,
            text: [
                newEmailNotifications.Sender,
                newEmailNotifications.Subject,
                newEmailNotifications.PreviewText,
            ],
            notificationType: NotificationTypes.NewEmail,
            originSmtpEmailAddress: newEmailNotifications.SenderSmtpEmailAddress,
            isFutureNotification: false,
            scheduledTime: null,
            itemId: newEmailNotifications.ItemId,
            originName: newEmailNotifications.Sender,
            upn: getUserConfiguration().SessionSettings?.UserPrincipalName,
            notificationActions: customAction,
            originDisplayImageUrl: '',
            isSilent: false,
        } as NotificationContext;
        raiseNotification(notificationContext);
    }
}

function getActionIconUrl(iconName: string): string {
    return `https:${getCdnUrl()}assets/native-host/notifications/${iconName}.ico`;
}
